const ORANGE = [234, 91, 42] as const
const GREEN = [0, 66, 59] as const
const REFERENCE_BACKGROUND = [255, 248, 243] as const
const DECK_BACKGROUND = [252, 251, 247] as const
const BACKGROUNDS = [
  [255, 255, 255],
  REFERENCE_BACKGROUND,
] as const

function clamp(value: number, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value))
}

function matchDeckBackground(red: number, green: number, blue: number) {
  const distance = Math.sqrt(
    (red - REFERENCE_BACKGROUND[0]) ** 2 +
      (green - REFERENCE_BACKGROUND[1]) ** 2 +
      (blue - REFERENCE_BACKGROUND[2]) ** 2,
  )

  if (distance >= 24) return [red, green, blue] as const

  const blend = clamp(1 - distance / 24)
  return DECK_BACKGROUND.map((channel, index) =>
    Math.round(blend * channel + (1 - blend) * [red, green, blue][index]),
  ) as [number, number, number]
}

function recolorPixel(red: number, green: number, blue: number) {
  let bestResidual = Number.POSITIVE_INFINITY
  let bestAlpha = 0
  let bestBackground: readonly [number, number, number] = BACKGROUNDS[0]

  BACKGROUNDS.forEach((background) => {
    const sourceVector = ORANGE.map((channel, index) => channel - background[index])
    const pixelVector = [red, green, blue].map((channel, index) => channel - background[index])
    const denominator = sourceVector.reduce((sum, channel) => sum + channel * channel, 0)
    const alpha = clamp(
      pixelVector.reduce((sum, channel, index) => sum + channel * sourceVector[index], 0) /
        denominator,
    )
    const reconstructed = ORANGE.map(
      (channel, index) => alpha * channel + (1 - alpha) * background[index],
    )
    const residual = Math.sqrt(
      (red - reconstructed[0]) ** 2 +
        (green - reconstructed[1]) ** 2 +
        (blue - reconstructed[2]) ** 2,
    )

    if (residual < bestResidual) {
      bestResidual = residual
      bestAlpha = alpha
      bestBackground = background
    }
  })

  const directDistance = Math.sqrt(
    (red - ORANGE[0]) ** 2 +
      (green - ORANGE[1]) ** 2 +
      (blue - ORANGE[2]) ** 2,
  )
  const isOrangeFamily = red > green * 1.45 && red > blue * 2.1 && green > blue * 0.95
  const isMatchedOrange =
    isOrangeFamily &&
    ((bestResidual < 34 && bestAlpha > 0.18) || directDistance < 72)

  if (!isMatchedOrange) return [red, green, blue] as const

  const alpha = directDistance < 48 ? 1 : bestAlpha
  return GREEN.map((channel, index) =>
    Math.round(alpha * channel + (1 - alpha) * bestBackground[index]),
  ) as [number, number, number]
}

export async function recolorOrangeToGreen(src: string) {
  const response = await fetch(src)
  if (!response.ok) throw new Error(`Unable to load image: ${response.status}`)

  const blob = await response.blob()
  const bitmap = await createImageBitmap(blob)
  const canvas = document.createElement('canvas')
  canvas.width = bitmap.width
  canvas.height = bitmap.height

  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) {
    bitmap.close()
    throw new Error('Canvas rendering is unavailable')
  }

  context.drawImage(bitmap, 0, 0)
  bitmap.close()

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
  const { data } = imageData

  for (let index = 0; index < data.length; index += 4) {
    if (data[index + 3] === 0) continue
    const [greenRed, greenGreen, greenBlue] = recolorPixel(
      data[index],
      data[index + 1],
      data[index + 2],
    )
    const [red, green, blue] = matchDeckBackground(greenRed, greenGreen, greenBlue)
    data[index] = red
    data[index + 1] = green
    data[index + 2] = blue
  }

  context.putImageData(imageData, 0, 0)
  return canvas.toDataURL('image/png')
}
